/**
 * Applying, for people who arrive from a shared link with no account.
 *
 * Three steps, in the order the work happens: who you are, then one sample
 * in your own voice, then a clear "we'll be in touch". The sample prompt is
 * written in the catalogue's own shape, so the reviewer hears exactly the
 * skill the job needs.
 *
 * `?source=linkedin` (or `utm_source`) on the link is recorded with the
 * application, so it is possible to tell which post brought people in.
 */
import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MarketingChrome } from "../components/MarketingChrome";
import { CountrySelect } from "../components/CountrySelect";
import { Waveform } from "../components/Waveform";
import { IconArrow, IconBack, IconCheck, IconPause, IconPlay, IconRefresh, IconTrash, MicBlackIcon } from "../components/Icons";
import { useToast } from "../hooks/useToast";
import { useRecorder } from "../hooks/useRecorder";
import { applications as applicationsApi } from "../api/endpoints";
import { useConfig } from "../store/config";
import { formatDuration, validateEmail } from "../lib/validation";
import type { ApplicationPrompt } from "../types/api";

type Step = "details" | "record" | "done";

function ReviewClock() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true">
      <path d="M7 12.25C9.8995 12.25 12.25 9.8995 12.25 7C12.25 4.1005 9.8995 1.75 7 1.75C4.1005 1.75 1.75 4.1005 1.75 7C1.75 9.8995 4.1005 12.25 7 12.25Z" stroke="#FFC83D" strokeWidth="1.51667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M7 4.08301V6.99967L9.04167 8.16634" stroke="#FFC83D" strokeWidth="1.51667" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ReviewEye() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M1.33203 7.99967C1.33203 7.99967 3.66536 3.33301 7.9987 3.33301C12.332 3.33301 14.6654 7.99967 14.6654 7.99967C14.6654 7.99967 12.332 12.6663 7.9987 12.6663C3.66536 12.6663 1.33203 7.99967 1.33203 7.99967Z" stroke="#FFC83D" strokeWidth="1.46667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M8 10C9.10457 10 10 9.10457 10 8C10 6.89543 9.10457 6 8 6C6.89543 6 6 6.89543 6 8C6 9.10457 6.89543 10 8 10Z" stroke="#FFC83D" strokeWidth="1.46667" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ReviewMail() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M12 3.33301H4C2.89543 3.33301 2 4.22844 2 5.33301V10.6663C2 11.7709 2.89543 12.6663 4 12.6663H12C13.1046 12.6663 14 11.7709 14 10.6663V5.33301C14 4.22844 13.1046 3.33301 12 3.33301Z" stroke="#FFC83D" strokeWidth="1.46667" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M2.66797 5L8.0013 9L13.3346 5" stroke="#FFC83D" strokeWidth="1.46667" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function ReviewTick() {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" width="31" height="31" viewBox="0 0 31 31" fill="none" aria-hidden="true">
      <path d="M6.35156 15.8772L12.0673 21.5929L24.1338 9.52637" stroke="white" strokeWidth="3.81048" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const MAX_NAME = 60;

function validateFullName(value: string): string | null {
  const name = value.trim();
  if (!name) return "Enter your name";
  if (name.length > MAX_NAME) return `Keep it under ${MAX_NAME} characters`;
  return null;
}

export function Apply() {
  const toast = useToast();
  const [searchParams] = useSearchParams();
  // Read once, on arrival: a later navigation should not change where an
  // application says it came from.
  const [source] = useState(
    () => searchParams.get("source") ?? searchParams.get("utm_source") ?? undefined,
  );

  const limits = useConfig((state) => state.config.recording);
  const recorder = useRecorder(limits.max_seconds);

  const [step, setStep] = useState<Step>("details");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [country, setCountry] = useState("");
  const [website, setWebsite] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [prompt, setPrompt] = useState<ApplicationPrompt | null>(null);
  const [promptError, setPromptError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [playing, setPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const errors = {
    name: validateFullName(name),
    email: validateEmail(email),
    country: country ? null : "Choose the country you'll be recording from",
  };
  const detailsValid = !errors.name && !errors.email && !errors.country;

  // Fetched up front, so the record step never opens on a spinner.
  useEffect(() => {
    let cancelled = false;
    applicationsApi
      .prompt()
      .then((result) => {
        if (!cancelled) setPrompt(result);
      })
      .catch((err: Error) => {
        if (!cancelled) setPromptError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  function continueToRecord(event: FormEvent) {
    event.preventDefault();
    setTouched({ name: true, email: true, country: true });
    if (!detailsValid) return;
    setStep("record");
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  const clip = recorder.clip;
  const tooShort = clip ? clip.durationSeconds < limits.min_seconds : false;

  async function submit() {
    if (!clip || !prompt || tooShort) return;
    setSending(true);
    setSendError(null);
    try {
      await applicationsApi.submit({
        name: name.trim(),
        email: email.trim(),
        country,
        promptId: prompt.id,
        blob: clip.blob,
        filename: `sample.${clip.extension}`,
        durationSeconds: clip.durationSeconds,
        source,
        website,
      });
      recorder.reset();
      setStep("done");
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const message = (err as Error).message;
      setSendError(message);
      toast.show(message, "error");
    } finally {
      setSending(false);
    }
  }

  function fieldClass(key: "name" | "email" | "country"): string {
    return touched[key] && errors[key] ? "field err" : "field";
  }

  const price = "$0.05";
  const firstName = name.trim().split(" ")[0] || "there";
  const shownTime =
    recorder.state === "recording"
      ? formatDuration(recorder.seconds)
      : clip
        ? formatDuration(clip.durationSeconds)
        : "00:00";

  function togglePlayback() {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play();
    else audio.pause();
  }

  return (
    <MarketingChrome back={{ to: "/", label: "Back to Inputry" }}>
      <div className="apply-page">
        <div className="apply-layout apply">
          <aside className="apply-pitch">
            <div className="kicker"><i /> Apply to contribute</div>
            <h1>
              Apply to become an <span>Inputry contributor</span>
            </h1>
            <p className="sub">Short voice notes. Paid through PayPal/Wise</p>
            <div className="pitch-facts">
              <div>
                <strong>{price}</strong>
                <span>Per approved voice note</span>
              </div>
              <div>
                <strong>Wise</strong>
                <span>Payments for approved contributions</span>
              </div>
              <div>
                <strong>Remote</strong>
                <span>Record on your phone or computer</span>
              </div>
            </div>
            <p className="apply-learn" style={{ marginTop: 18 }}>
              <Link to="/how-it-works" className="text-link">
                Learn more about Inputry <IconArrow width={14} height={14} />
              </Link>
            </p>
          </aside>

          <div className="fact-chips">
            <div><strong>{price}</strong><span>Per approved note</span></div>
            <div><strong>PayPal</strong><span>Payments</span></div>
            <div><strong>Remote</strong><span>Phone or laptop</span></div>
          </div>

          {step === "details" && (
            <form className="panel-card" onSubmit={continueToRecord} noValidate>
              <div className="progress-label"><span>STEP 1 OF 2</span></div>
              <div className="bar"><i style={{ width: "50%" }} /></div>
              <h2 style={{ marginTop: 0 }}>Tell us about yourself</h2>
              <div className={fieldClass("name")}>
                <label htmlFor="apply-name">Full name</label>
                <input
                  id="apply-name"
                  value={name}
                  placeholder="Your full name"
                  autoComplete="name"
                  maxLength={MAX_NAME}
                  onChange={(event) => setName(event.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, name: true }))}
                />
                <div className="err-msg">{touched.name ? errors.name : ""}</div>
              </div>
              <div className={fieldClass("email")}>
                <label htmlFor="apply-email">Email address</label>
                <input
                  id="apply-email"
                  type="email"
                  value={email}
                  placeholder="Enter email"
                  autoComplete="email"
                  onChange={(event) => setEmail(event.target.value)}
                  onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                />
                <div className="err-msg">{touched.email ? errors.email : ""}</div>
              </div>
              <div className={fieldClass("country")}>
                <label htmlFor="country">Country</label>
                <CountrySelect value={country} onChange={setCountry} />
                <div className="hint">Your country helps determine eligibility and contributor rates.</div>
                <div className="err-msg">{touched.country ? errors.country : ""}</div>
              </div>
              <div className="apply-trap" aria-hidden="true">
                <label htmlFor="apply-website">Website</label>
                <input
                  id="apply-website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={website}
                  onChange={(event) => setWebsite(event.target.value)}
                />
              </div>
              <button type="submit" className="btn btn-primary">
                Continue <IconArrow width={16} height={16} />
              </button>
              <p className="fine">
                Already invited? <Link to="/signup">Create your account</Link>
              </p>
            </form>
          )}

          {step === "record" && (
            <div className="panel-card">
              <div className="progress-label"><span>STEP 2 OF 2</span></div>
              <div className="bar"><i style={{ width: "100%" }} /></div>
              <h2 style={{ marginTop: 0 }}>Record a short voice sample</h2>
              {promptError && !prompt ? (
                <div className="empty">
                  <h3>We couldn't load your sample</h3>
                  <p>{promptError}</p>
                </div>
              ) : (
                <>
                  <div className="prompt-box">
                    <small>PROMPT</small>
                    <p>{prompt?.text ?? "Loading your prompt…"}</p>
                  </div>
                  <div className="capture">
                    <div className={`capture-time ${recorder.state === "recording" ? "live" : ""}`}>
                      {shownTime}
                    </div>
                    {recorder.state === "recording" ? (
                      <div className="status-pill warn">Recording</div>
                    ) : clip ? (
                      <div className="status-pill ok"><IconCheck width={14} height={14} /> Recording complete</div>
                    ) : (
                      <div className="legal-copy">Maximum {formatDuration(limits.max_seconds)}</div>
                    )}
                    <Waveform
                      levels={recorder.levels}
                      active={recorder.state === "recording" || Boolean(clip)}
                    />
                    {recorder.error && <div className="form-error">{recorder.error}</div>}
                    {tooShort && (
                      <div className="form-error">
                        That's under {limits.min_seconds} seconds — record a longer take.
                      </div>
                    )}
                    {sendError && <div className="form-error">{sendError}</div>}
                    {clip && (
                      <audio
                        ref={audioRef}
                        hidden
                        src={clip.url}
                        preload="metadata"
                        onPlay={() => setPlaying(true)}
                        onPause={() => setPlaying(false)}
                        onEnded={() => setPlaying(false)}
                      />
                    )}
                    {recorder.state === "recording" ? (
                      <div className="inline-actions">
                        {recorder.paused ? (
                          <button type="button" className="btn btn-ghost" onClick={recorder.resume}>Resume</button>
                        ) : (
                          <button type="button" className="btn btn-ghost" onClick={recorder.pause}>Pause</button>
                        )}
                        <button type="button" className="btn btn-primary" style={{ width: "auto" }} onClick={recorder.stop}>
                          Stop recording
                        </button>
                      </div>
                    ) : clip ? (
                      <div className="take-actions">
                        <button
                          type="button"
                          className="play-orb"
                          aria-label={playing ? "Pause recording" : "Play recording"}
                          onClick={togglePlayback}
                        >
                          {playing ? <IconPause width={18} height={18} /> : <IconPlay width={18} height={18} />}
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={() => void recorder.start()} disabled={sending}>
                          <IconRefresh width={16} height={16} /> Record again
                        </button>
                        <button type="button" className="btn btn-ghost" onClick={recorder.reset} disabled={sending}>
                          <IconTrash width={16} height={16} /> Delete
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="mic-orb"
                        onClick={() => void recorder.start()}
                        disabled={!prompt || recorder.state === "encoding" || recorder.state === "requesting"}
                      >
                        <MicBlackIcon />
                      </button>
                    )}
                    {!clip && recorder.state !== "recording" && (
                      <div className="legal-copy">
                        {recorder.state === "requesting" ? "Starting…" : "Start recording"}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className="btn btn-primary mt-16"
                    onClick={() => void submit()}
                    disabled={!clip || sending || tooShort || !prompt}
                  >
                    {sending ? "Sending…" : "Submit sample"} <IconArrow width={16} height={16} />
                  </button>
                </>
              )}
            </div>
          )}

          {step === "done" && (
            <div className="panel-card thanks-card apply-thanks">
              <p className="thanks-lead">Your recording has been sent for review.</p>
              <div className="check-orb"><ReviewTick /></div>
              <h1>Thanks {firstName}</h1>
              <p className="sub">
                We'll listen to your sample and email {email.trim()} with our answer. If you're
                accepted, that email has your invite and a link to create your account.
              </p>
              <div className="thanks-desktop">
                <div className="in-review"><ReviewClock /> In review</div>
                <Link to="/" className="btn btn-primary">
                  <IconBack width={16} height={16} /> Back to Inputry
                </Link>
              </div>
              <div className="thanks-mobile">
                <div className="done-row"><IconCheck width={16} height={16} /> Application</div>
                <div className="done-row"><IconCheck width={16} height={16} /> Voice sample</div>
                <div className="done-row pending"><ReviewEye /> Team review</div>
                <div className="done-row pending"><ReviewMail /> Email invitation</div>
                <Link to="/" className="btn btn-ghost">
                  Back to Inputry <IconArrow width={16} height={16} />
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </MarketingChrome>
  );
}
